import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, LogOut, Settings, ChevronDown } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import SmithersPresenceDot from "./SmithersPresenceDot";
import SignalStackIcon from "./notifications/SignalStackIcon";

export default function PortalNav({ onSmithers }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    return (
        <header
            data-testid="portal-nav"
            className="
                sticky top-0 z-30
                bg-tca-bg-deep/80 backdrop-blur-xl
                border-b border-tca-cyan/15
            "
        >
            <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center gap-4">
                <Link to="/portal" className="flex items-center gap-2.5 shrink-0" data-testid="portal-nav-logo">
                    <img src="/brand/logo-stacked.svg" alt="" className="h-7 w-7" />
                    <span className="hidden sm:inline font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan">
                        Operator
                    </span>
                </Link>

                <div className="hidden md:flex flex-1 max-w-md relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-tca-ink-muted" />
                    <input
                        type="search"
                        placeholder="Search modules · resources · files"
                        data-testid="portal-search"
                        className="
                            w-full pl-9 pr-4 py-2 text-sm
                            bg-tca-bg-panel/60 border border-tca-cyan/15
                            placeholder-tca-ink-muted/55 text-tca-ink
                            focus:outline-none focus:border-tca-cyan/55
                            rounded-md
                        "
                    />
                </div>

                <div className="flex-1 md:hidden" />

                <SmithersPresenceDot onClick={onSmithers} />

                <div className="hidden sm:inline-flex">
                    <SignalStackIcon />
                </div>

                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setMenuOpen((s) => !s)}
                        data-testid="portal-nav-avatar"
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-tca-cyan/22 hover:border-tca-cyan/55 transition-all"
                    >
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-tca-cyan to-tca-cyan-deep flex items-center justify-center text-[10px] font-mono-tight text-tca-bg-deep font-bold">
                            {(user?.email || "?")[0].toUpperCase()}
                        </div>
                        <ChevronDown size={11} className="text-tca-ink-muted" />
                    </button>
                    <AnimatePresence>
                        {menuOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -6 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -6 }}
                                transition={{ duration: 0.18 }}
                                data-testid="portal-account-menu"
                                onMouseLeave={() => setMenuOpen(false)}
                                className="absolute right-0 mt-2 w-60 border border-tca-cyan/22 bg-tca-bg-panel/95 backdrop-blur-xl rounded-md overflow-hidden"
                            >
                                <div className="px-4 py-3 border-b border-tca-ink-dim/30">
                                    <div className="font-mono-tight text-[9px] uppercase tracking-[0.28em] text-tca-cyan/75">Operator</div>
                                    <div className="text-sm text-tca-ink truncate mt-0.5">{user?.email}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setMenuOpen(false)}
                                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-tca-ink hover:text-tca-cyan hover:bg-tca-cyan/5"
                                >
                                    <Settings size={13} /> Settings
                                </button>
                                <button
                                    type="button"
                                    onClick={async () => { setMenuOpen(false); await logout(); nav("/"); }}
                                    data-testid="portal-nav-logout"
                                    className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-tca-ink-muted hover:text-tca-magenta-accent hover:bg-tca-magenta-accent/5 border-t border-tca-ink-dim/30"
                                >
                                    <LogOut size={13} /> Sign out
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </header>
    );
}
